import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

/**
 * Recently played — a short MRU list of station keys per kind, used to
 * populate the "Continue watching" and "Recently played" rows on the
 * Discover tab. Persisted in localStorage, capped at 8 entries.
 */

export type RecentKind = "tv" | "radio";
const CAP = 8;
const STORAGE_KEY = "orvis:recent";

interface Persisted {
  tv: string[];
  radio: string[];
}

interface RecentCtx {
  push: (kind: RecentKind, id: string) => void;
  list: (kind: RecentKind) => string[];
}

const Ctx = createContext<RecentCtx | undefined>(undefined);

function read(): Persisted {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { tv: [], radio: [] };
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return { tv: [], radio: [] };
    const p = parsed as Record<string, unknown>;
    return {
      tv: Array.isArray(p["tv"]) ? (p["tv"] as string[]).filter((s): s is string => typeof s === "string") : [],
      radio: Array.isArray(p["radio"]) ? (p["radio"] as string[]).filter((s): s is string => typeof s === "string") : [],
    };
  } catch {
    return { tv: [], radio: [] };
  }
}

function write(next: Persisted) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch { /* ignore */ }
}

export function RecentlyPlayedProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<Persisted>(() => read());
  useEffect(() => write(state), [state]);

  const push = useCallback((kind: RecentKind, id: string) => {
    setState((prev) => {
      const filtered = prev[kind].filter((x) => x !== id);
      const next = [id, ...filtered].slice(0, CAP);
      return { ...prev, [kind]: next };
    });
  }, []);

  const list = useCallback((kind: RecentKind) => state[kind], [state]);

  const value = useMemo<RecentCtx>(() => ({ push, list }), [push, list]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useRecentlyPlayed(): RecentCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error("useRecentlyPlayed must be used inside <RecentlyPlayedProvider>");
  return v;
}
